import { useMemo } from 'react';
import { Box, useTheme } from '@mui/material';
import { Marked } from 'marked';
import DOMPurify from 'dompurify';
import { tokens } from '../../global/theme/tokens';

const markdown = new Marked({ gfm: true, breaks: true, async: false });

// Model output is untrusted: render markdown, sanitize, open links in a new tab.
// Images are dropped, an injected prompt could leak data through image URLs.
const SANITIZE_OPTIONS = { FORBID_TAGS: ['img', 'style', 'form', 'input'] };

export const AiMarkdown = ({ content }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);

  const html = useMemo(
    () =>
      DOMPurify.sanitize(markdown.parse(content || ''), SANITIZE_OPTIONS).replace(
        /<a /g,
        '<a target="_blank" rel="noopener noreferrer" ',
      ),
    [content],
  );

  return (
    <Box
      className="ai-markdown"
      dangerouslySetInnerHTML={{ __html: html }}
      sx={{
        color: colors.text,
        fontSize: '0.875rem',
        lineHeight: 1.6,
        wordBreak: 'break-word',
        '& > :first-of-type': { mt: 0 },
        '& > :last-child': { mb: 0 },
        '& p': { my: 1 },
        '& h1, & h2, & h3, & h4': {
          mt: 2,
          mb: 1,
          fontWeight: 600,
          lineHeight: 1.3,
        },
        '& h1': { fontSize: '1.15rem' },
        '& h2': { fontSize: '1.05rem' },
        '& h3, & h4': { fontSize: '0.95rem' },
        '& ul, & ol': { my: 1, pl: 2.5 },
        '& ul': { listStyle: 'disc' },
        '& ol': { listStyle: 'decimal' },
        '& li': { my: 0.25 },
        '& a': { color: colors.info, textDecoration: 'underline' },
        '& code': {
          fontFamily: 'Source Code Pro, monospace',
          fontSize: '0.8rem',
          backgroundColor: colors.bgInput,
          borderRadius: '4px',
          px: 0.5,
        },
        '& pre': {
          my: 1,
          p: 1.5,
          overflowX: 'auto',
          backgroundColor: colors.bgInput,
          borderRadius: '8px',
        },
        '& pre code': { backgroundColor: 'transparent', p: 0 },
        '& blockquote': {
          my: 1,
          pl: 1.5,
          borderLeft: `3px solid ${colors.info}`,
          opacity: 0.85,
        },
        '& table': {
          my: 1,
          borderCollapse: 'collapse',
          display: 'block',
          overflowX: 'auto',
          fontSize: '0.8rem',
        },
        '& th, & td': {
          border: `1px solid ${colors.bgInput}`,
          px: 1,
          py: 0.5,
          textAlign: 'left',
        },
        '& th': { backgroundColor: colors.bgInput, fontWeight: 600 },
        '& hr': { my: 2, borderColor: colors.bgInput },
      }}
    />
  );
};
